import type { BoardModel } from "../domain/types";

// MacBook Air M1 2020 (A2337) — carte 820-02016 / MLB X1757 (Rev 4.0.0).
// Apple Silicon : PAS de SMC discret, le PMU Apple (343Sxxxx) génère la
// quasi-totalité des rails. États M1 (AON/AWAKE/S2/S1) mappés sur PowerState :
//   AON → always_on, S2/S1/AWAKE → S0 (système en marche), USB-C in → G3.
// Tensions dérivées du nom de net (le schéma encode V) ; rails PPVDD_* = cœurs
// SoC à VID variable → valeurs indicatives à VALIDER sur boardview (FlexBV).
// À fusionner dans SEED_BOARDS (src/data/boards.seed.ts).

export const BOARD_820_02016: BoardModel = {
  id: "820-02016",
  brand: "Apple",
  model: 'MacBook Air M1 2020 (A2337)',
  aliases: ["A2337", "MacBookAir10,1", "MLB X1757", "820-02016", "M1 Air"],
  updated_at: new Date().toISOString(),
  rails: [
    {
      name: "PPVBUS_USBC0",
      state: "G3",
      nominal_v: 20.0,
      tolerance_v: 1.0,
      source_component: "Connecteur USB-C 0 (négociation PD)",
      parent: null,
      notes: "VBUS USB-C (5–20 V selon PD). Entrée alim. 2 ports : USBC0 et USBC1.",
    },
    {
      name: "PPDCIN_AONSW",
      state: "G3",
      nominal_v: 20.0,
      tolerance_v: 1.0,
      source_component: "Étage d'entrée (load switch) depuis VBUS USB-C",
      parent: "PPVBUS_USBC0",
      notes: "Tension d'entrée commutée vers le chargeur (PPCHGR).",
    },
    {
      name: "PPBUS_AON",
      state: "always_on",
      nominal_v: 11.5,
      tolerance_v: 1.5,
      source_component: "Chargeur Apple + MOSFETs (PPVBAT/PPCHGR)",
      enabled_by: "PMU",
      parent: "PPDCIN_AONSW",
      notes: "Rail principal (batterie 3S ≈ 11.4 V, jusqu'à ~13 V en charge). Présent batterie OU USB-C. Départ de TOUT diagnostic d'alim.",
    },
    {
      name: "PP3V8_AON_VDDMAIN",
      state: "always_on",
      nominal_v: 3.8,
      tolerance_v: 0.15,
      source_component: "Buck always-on depuis PPBUS_AON",
      parent: "PPBUS_AON",
      notes: "Rail always-on principal alimentant le PMU (équivalent du rail SMC sur Intel). Absent → PMU mort, aucun réveil.",
    },
    {
      name: "PP1V8_AON_MPMU",
      state: "always_on",
      nominal_v: 1.8,
      tolerance_v: 0.08,
      source_component: "LDO/buck always-on du PMU (MPMU)",
      parent: "PP3V8_AON_VDDMAIN",
      notes: "Logique always-on du PMU principal.",
    },
    {
      name: "PP1V8_AON",
      state: "always_on",
      nominal_v: 1.8,
      tolerance_v: 0.08,
      source_component: "Rail 1.8 V always-on",
      parent: "PP3V8_AON_VDDMAIN",
    },
    {
      name: "PP3V3_AON",
      state: "always_on",
      nominal_v: 3.3,
      tolerance_v: 0.1,
      source_component: "Rail 3.3 V always-on",
      parent: "PP3V8_AON_VDDMAIN",
    },
    {
      name: "PP5V_S2",
      state: "S0",
      nominal_v: 5.0,
      tolerance_v: 0.2,
      source_component: "Buck 5 V (état S2 = système allumé)",
      enabled_by: "PMU (appui power)",
      parent: "PPBUS_AON",
      notes: "Apparaît à l'allumage. Absent → séquence bloquée au PMU.",
    },
    {
      name: "PP3V3_S2",
      state: "S0",
      nominal_v: 3.3,
      tolerance_v: 0.1,
      source_component: "Buck 3.3 V S2",
      parent: "PP5V_S2",
    },
    {
      name: "PP1V8_S2",
      state: "S0",
      nominal_v: 1.8,
      tolerance_v: 0.08,
      source_component: "Buck/LDO 1.8 V S2",
      parent: "PP3V3_S2",
      notes: "Rail 1.8 V S2 très ramifié.",
    },
    {
      name: "PP1V25_S2",
      state: "S0",
      nominal_v: 1.25,
      tolerance_v: 0.06,
      source_component: "Buck 1.25 V S2",
      parent: "PP3V3_S2",
    },
    {
      name: "PPVDD_ECPU_AWAKE",
      state: "S0",
      nominal_v: 0.8,
      tolerance_v: 0.2,
      source_component: "PMU — VR cœurs efficacité (E-cores) M1",
      enabled_by: "PMU (séquence d'allumage)",
      parent: "PPBUS_AON",
      notes: "VID variable. Présent seulement système allumé.",
    },
    {
      name: "PPVDD_PCPU_AWAKE",
      state: "S0",
      nominal_v: 0.9,
      tolerance_v: 0.25,
      source_component: "PMU — VR cœurs performance (P-cores) M1",
      parent: "PPBUS_AON",
      notes: "VID variable.",
    },
    {
      name: "PPVDD_GPU_AWAKE",
      state: "S0",
      nominal_v: 0.85,
      tolerance_v: 0.2,
      source_component: "PMU — VR GPU intégré M1",
      parent: "PPBUS_AON",
      notes: "VID variable.",
    },
    {
      name: "PPVDD_SOC_S1",
      state: "S0",
      nominal_v: 0.9,
      tolerance_v: 0.15,
      source_component: "PMU — VR SoC/uncore",
      parent: "PPBUS_AON",
    },
    {
      name: "PP1V06_S2SW_DRAM",
      state: "S0",
      nominal_v: 1.06,
      tolerance_v: 0.06,
      source_component: "VR DRAM (LPDDR4X intégrée au package M1)",
      parent: "PP3V3_S2",
      notes: "Alim mémoire on-package.",
    },
    {
      name: "PP1V25_AWAKE_NAND",
      state: "S0",
      nominal_v: 1.25,
      tolerance_v: 0.06,
      source_component: "VR NAND (SSD soudé)",
      parent: "PP3V3_S2",
      notes: "Avec PP0V88_AWAKE_NAND et PP2V5_AWAKE_NAND pour le stockage.",
    },
    {
      name: "PP3V3_SW_LCD",
      state: "S0",
      nominal_v: 3.3,
      tolerance_v: 0.1,
      source_component: "Load switch vers l'écran",
      parent: "PP3V3_S2",
      notes: "Alim écran/rétroéclairage (avec l'étage backlight).",
    },
  ],
  measure_points: [
    {
      label: "VBUS USB-C (PPVBUS_USBC0/1)",
      rail: "PPVBUS_USBC0",
      type: "voltage",
      expected: "5–20 V (chargeur USB-C branché, selon PD)",
      location: "Près des connecteurs USB-C / port controllers",
      notes: "Tester les DEUX ports. Absente sur batterie seule — normal.",
    },
    {
      label: "Bus principal PPBUS_AON",
      rail: "PPBUS_AON",
      type: "voltage",
      expected: "≈ 11.4 V (batterie) à ~13 V (en charge)",
      notes: "Si 0 V → remonter vers chargeur/MOSFETs. Présent batterie OU USB-C.",
    },
    {
      label: "PPBUS_AON vers GND (hors tension)",
      rail: "PPBUS_AON",
      type: "resistance",
      expected: "> quelques centaines d'Ω (pas de court franc)",
      notes: "< 5 Ω = court rail principal → injection douce + caméra thermique.",
    },
    {
      label: "PP3V8_AON_VDDMAIN",
      rail: "PP3V8_AON_VDDMAIN",
      type: "voltage",
      expected: "3.7 – 3.9 V",
      notes: "Always-on, alim PMU (équivalent SMC). Absent → PMU mort, aucun réveil.",
    },
    {
      label: "PP1V8_AON_MPMU",
      rail: "PP1V8_AON_MPMU",
      type: "voltage",
      expected: "1.72 – 1.88 V",
      notes: "Logique PMU always-on.",
    },
    {
      label: "PP5V_S2",
      rail: "PP5V_S2",
      type: "voltage",
      expected: "4.85 – 5.15 V",
      notes: "Apparaît à l'appui power. Absent → blocage au PMU.",
    },
    {
      label: "PPVDD_SOC_S1 / cœurs vers GND (hors tension)",
      rail: "PPVDD_SOC_S1",
      type: "resistance",
      expected: "> ~5 Ω (selon VR)",
      notes: "Très bas = court sur un rail cœur SoC (PMU/M1) → liquide ou PMU.",
    },
  ],
  symptoms: [
    {
      id: "dead_board",
      label: "Carte morte (aucun démarrage)",
      description: "Aucune réaction, pas de consommation.",
      checklist: [
        "Mesurer VBUS USB-C (les 2 ports) — présence tension d'entrée",
        "Mesurer PPBUS_AON — rail principal (≈ 11.5 V)",
        "Si PPBUS absent : vérifier chargeur + MOSFETs (PPCHGR/PPVBAT)",
        "Mesurer PP3V8_AON_VDDMAIN (alim PMU) — rail clé",
        "Mesurer PP1V8_AON_MPMU (logique PMU)",
        "Appui power → PP5V_S2 doit apparaître",
      ],
    },
    {
      id: "no_charge",
      label: "Ne charge pas",
      description: "Batterie non chargée / USB-C non reconnu.",
      checklist: [
        "Mesurer VBUS USB-C — négociation PD OK ?",
        "Vérifier les port controllers USB-C et lignes CC",
        "Contrôler le chargeur Apple + MOSFETs côté PPBUS_AON",
      ],
    },
    {
      id: "overcurrent",
      label: "Surconsommation / chauffe",
      description: "Consommation anormale, point chaud.",
      checklist: [
        "Mesurer résistance PPBUS_AON vers GND",
        "Mesurer résistance des rails cœur SoC (PPVDD_*) vers GND",
        "Injection de tension douce + caméra thermique (PMU et M1 = suspects)",
        "Rechercher dégât liquide / corrosion sous le PMU",
      ],
    },
    {
      id: "no_video",
      label: "Pas d'image",
      description: "Démarre mais écran noir.",
      checklist: [
        "Confirmer rétroéclairage (lampe torche sur l'écran)",
        "Mesurer PP3V3_SW_LCD et l'étage rétroéclairage",
        "Contrôler le connecteur / la nappe d'écran",
        "Tester une sortie externe (USB-C/DP) pour isoler carte vs dalle",
      ],
    },
    {
      id: "no_boot_seq",
      label: "Bloque sur la séquence d'alim",
      description: "PPBUS et PP3V8_AON présents mais pas de mise en marche (S2).",
      checklist: [
        "Vérifier l'apparition de PP5V_S2 / PP3V3_S2 à l'appui power",
        "Suivre les rails cœur : PPVDD_ECPU/PCPU/GPU/SOC_AWAKE",
        "Mesurer PP1V06_S2SW_DRAM (mémoire) et les rails NAND",
        "Suspecter le PMU Apple (343Sxxxx) si la séquence ne démarre pas",
      ],
    },
  ],
};
